import { ChevronDown, ShoppingCart, Store, X, Sparkles } from "lucide-react";
import type { Store as ClientStore } from "@/features/store/types";
import type { DisplayItem } from "@/features/quotation/clientDisplay";
import type { EnrichedQuotationItem } from "@/features/quotation/types";
import {
  getQuotationBasePriceSuffix,
  getQuotationBasePriceValue,
  PRICE_PER_UNIT_SUFFIX,
} from "@/features/quotation/unitLabels";
import { formatBRL } from "@/lib/timeAgo";
import {
  getDefaultQuantity,
  getQuantityInputMode,
  getQuantityInputStep,
  getWholeDirectKgQuantityInputAttributes,
  isDirectKgUnit,
} from "@/lib/quantity";

export type QuotationRowProps = {
  expandedItemId: string | null;
  selectedStoreId: string;
  stores: ClientStore[];
  quantity: string;
  addError: string | null;
  addSuccessId: string | null;
  isAdding: boolean;
  addDisabled?: boolean;
  confirmDisabled?: boolean;
  addHint?: string;
  addReadOnly?: boolean;
  onExpandItem: (itemId: string) => void;
  onStoreChange: (storeId: string) => void;
  onQuantityChange: (quantity: string) => void;
  onQuantityBlur: (item: EnrichedQuotationItem) => void;
  onConfirmAdd: (itemId: string) => void;
  getItemActionLabel?: (itemId: string) => "Adicionar" | "Atualizar";
};

function StoreField({
  stores,
  selectedStoreId,
  onStoreChange,
}: Pick<QuotationRowProps, "stores" | "selectedStoreId" | "onStoreChange">) {
  return (
    <span className="lnk-store-field">
      <Store size={17} />
      {stores.length <= 1 ? (
        <span>{stores[0]?.name ?? "Nenhuma loja disponível"}</span>
      ) : (
        <select
          aria-label="Loja"
          value={selectedStoreId}
          onChange={(event) => onStoreChange(event.target.value)}
        >
          <option value="">Selecione...</option>
          {stores.map((store) => (
            <option key={store.id} value={store.id}>
              {store.name}
            </option>
          ))}
        </select>
      )}
      <ChevronDown size={17} />
    </span>
  );
}

function ProductRow({
  item,
  offer,
  ...props
}: {
  item: Extract<DisplayItem, { isUnavailable?: false }>;
  offer?: boolean;
} & QuotationRowProps) {
  const expanded = props.expandedItemId === item.id && !props.addReadOnly;
  const added = props.addSuccessId === item.id;
  const disabled = props.isAdding || props.addDisabled || props.addReadOnly;
  const actionLabel = props.getItemActionLabel?.(item.id) ?? "Adicionar";
  const unit = getQuotationBasePriceSuffix(item.unit_type);
  const packageLabel = item.kg_per_box
    ? `${item.unit_type.startsWith("sc") ? "Saco" : "Caixa"} · ${item.kg_per_box} kg`
    : unit;
  return (
    <article
      className={`lnk-product-row cotacao-product-card ${offer ? "is-offer" : ""} ${expanded ? "is-expanded" : ""}`}
    >
      <div className="lnk-product-summary">
        <div className="lnk-product-copy">
          <div className="lnk-product-title-line">
            <strong>{item.product_name}</strong>
          </div>
          <span>{item.supplier_name}</span>
          <small>{packageLabel}</small>
        </div>
        <div className="lnk-product-price">
          <strong>{formatBRL(getQuotationBasePriceValue(item))}</strong>
          {!isDirectKgUnit(item.unit_type) && (
            <span>
              {formatBRL(item.price_per_unit)} /{" "}
              {(
                PRICE_PER_UNIT_SUFFIX[item.unit_type] ?? item.unit_type
              ).toLowerCase()}
            </span>
          )}
        </div>
        {!props.addReadOnly && (
          <button
            type="button"
            className={`lnk-add-button ${added ? "is-added" : ""}`}
            aria-label={`${expanded ? "Cancelar" : actionLabel} ${item.product_name}`}
            disabled={disabled}
            onClick={() => props.onExpandItem(item.id)}
          >
            {expanded ? (
              <X size={16} />
            ) : added ? (
              "Adicionado"
            ) : (
              <>
                <ShoppingCart size={16} /> {actionLabel}
              </>
            )}
          </button>
        )}
      </div>
      <div
        className={`lnk-add-panel-wrap ${expanded ? "is-open" : ""}`}
        inert={!expanded}
      >
        <div className="lnk-add-panel" aria-hidden={!expanded}>
          <label>
            <span>Loja</span>
            {expanded ? (
              <StoreField
                stores={props.stores}
                selectedStoreId={props.selectedStoreId}
                onStoreChange={props.onStoreChange}
              />
            ) : (
              <span className="lnk-store-field" />
            )}
          </label>
          <label>
            <span>Quantidade</span>
            {expanded ? (
              <input
                type="number"
                aria-label="Qtd"
                {...(isDirectKgUnit(item.unit_type)
                  ? getWholeDirectKgQuantityInputAttributes()
                  : {
                      min: getDefaultQuantity(item.unit_type),
                      step: getQuantityInputStep(item.unit_type),
                      inputMode: getQuantityInputMode(item.unit_type),
                    })}
                value={props.quantity}
                onChange={(event) => props.onQuantityChange(event.target.value)}
                onBlur={() => props.onQuantityBlur(item)}
                onFocus={(event) => event.target.select()}
              />
            ) : (
              <span className="lnk-quantity-placeholder" />
            )}
          </label>
          <button
            type="button"
            disabled={disabled || props.confirmDisabled}
            tabIndex={expanded ? 0 : -1}
            onClick={() => props.onConfirmAdd(item.id)}
          >
            {actionLabel}
          </button>
          {props.addHint && expanded && <p role="status">{props.addHint}</p>}
          {props.addError && expanded && <p role="alert">{props.addError}</p>}
        </div>
      </div>
    </article>
  );
}

export function QuotationCategorySection({
  category,
  label,
  variant = "category",
  items,
  expanded,
  onToggleCategory,
  ...rowProps
}: {
  category: string;
  label: string;
  variant?: "category" | "offers";
  items: DisplayItem[];
  expanded: boolean;
  onToggleCategory: (category: string) => void;
} & QuotationRowProps) {
  const available = items.filter((item) => !item.isUnavailable);
  if (variant === "offers")
    return (
      <section className="lnk-offers-section quotation-offers-section">
        <button
          className="lnk-offers-toggle"
          aria-expanded={expanded}
          onClick={() => onToggleCategory(category)}
        >
          <div>
            <span className="lnk-kicker offer">
              <Sparkles size={14} /> DESTAQUES DO DIA
            </span>
            <h2>Ofertas</h2>
          </div>
          <span className="lnk-offers-count">{available.length} produtos</span>
          <i>
            <ChevronDown size={21} />
          </i>
        </button>
        <div
          className={`lnk-offers-content ${expanded ? "is-open" : ""}`}
          inert={!expanded}
          aria-hidden={!expanded}
        >
          <div>
            <div className="lnk-ledger lnk-offer-ledger quotation-offers-ledger">
              {available.map((item) => (
                <ProductRow
                  key={item.id}
                  item={item as EnrichedQuotationItem}
                  offer
                  {...rowProps}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  return (
    <section className={`lnk-category ${expanded ? "is-open" : ""}`}>
      <button
        className="lnk-category-toggle cat-header"
        aria-expanded={expanded}
        onClick={() => onToggleCategory(category)}
      >
        <h3>{label}</h3>
        <span>
          {available.length} {available.length === 1 ? "item" : "itens"}
          <i>
            <ChevronDown size={18} />
          </i>
        </span>
      </button>
      <div
        className="lnk-category-content"
        inert={!expanded}
        aria-hidden={!expanded}
      >
        <div>
          <div className="lnk-ledger">
            {available.map((item) => (
              <ProductRow
                key={item.id}
                item={item as EnrichedQuotationItem}
                {...rowProps}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function UnavailableSection({
  groups,
  dateLabel,
  expanded,
  onToggle,
  openGroups,
  onToggleGroup,
}: {
  groups: { category: string; label: string; items: DisplayItem[] }[];
  dateLabel?: string;
  expanded: boolean;
  onToggle: () => void;
  openGroups: Set<string>;
  onToggleGroup: (category: string) => void;
}) {
  const count = groups.reduce((sum, group) => sum + group.items.length, 0);
  if (!count) return null;
  return (
    <section
      className={`lnk-category lnk-unavailable-section ${expanded ? "is-open" : ""}`}
    >
      <button
        className="lnk-category-toggle"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <h3>
          {dateLabel ? `Indisponíveis em ${dateLabel}` : "Indisponíveis hoje"}
        </h3>
        <span>
          {count} itens
          <i>
            <ChevronDown size={18} />
          </i>
        </span>
      </button>
      <div
        className="lnk-category-content"
        inert={!expanded}
        aria-hidden={!expanded}
      >
        <div className="lnk-unavailable-groups">
          {groups
            .filter(({ items }) => items.length)
            .map(({ category, label, items }) => (
              <div
                key={category}
                className={`lnk-unavailable-group ${openGroups.has(category) ? "is-open" : ""}`}
              >
                <button
                  aria-expanded={openGroups.has(category)}
                  onClick={() => onToggleGroup(category)}
                >
                  <h4>{label}</h4>
                  <ChevronDown size={16} />
                </button>
                <div
                  className="lnk-unavailable-group-content"
                  inert={!openGroups.has(category)}
                  aria-hidden={!openGroups.has(category)}
                >
                  <div>
                    {items.map((item) => (
                      <div className="lnk-unavailable-row" key={item.id}>
                        <strong>{item.product_name}</strong>
                        <span>Indisponível</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </section>
  );
}
